import {useAccessToken, useIsBankAdminOrOperationsUser, useUserClaimsData} from "../../services/auth"
import React from "react"
import TitleBar from "../../components/TitleBar/TitleBar"
import {MainSection} from "../../containers/MainSection/MainSection"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {getByToken} from "../../resources/bankSettings"
import classNames from "classnames"
import {useQueryState} from "use-location-state"
import {BankSettings} from "../../resources/bankSettings"
import LegalDocs from "./LegalDocs"
import SamlSettings from "./SamlSettings"
import RiskSettings from "./RiskSettings/RiskSettings"
import PaymentRules from "./PaymentRules"
import {isLocalEnv, isSandboxEnv} from "../../utilities/environment"

type BankSettingsTabs = "Legal" | "SAML" | "Risk Settings" | "Payment Rules"

const sandboxBankIds = ["20", "26"]

function TabContent({
	currentTab,
	bankSettings,
	refresh,
}: {
	currentTab: BankSettingsTabs
	bankSettings: BankSettings
	refresh: () => void
}) {
	if (currentTab == "Legal") {
		return <LegalDocs bankSettings={bankSettings} refresh={refresh} />
	} else if (currentTab == "SAML") {
		return <SamlSettings bankSettings={bankSettings} refresh={refresh} />
	} else if (currentTab == "Risk Settings") {
		return <RiskSettings />
	} else if (currentTab == "Payment Rules") {
		return <PaymentRules bankSettings={bankSettings} refresh={refresh} />
	}

	return null
}

function BankSettingsInternal({
	bankSettings,
	refresh,
	currentTab,
	setCurrentTab,
}: {
	bankSettings: BankSettings
	refresh: () => void
	currentTab: BankSettingsTabs
	setCurrentTab: (tab: BankSettingsTabs) => void
}) {
	const isBankAdminOrOperationsUser = useIsBankAdminOrOperationsUser()
	const claims = useUserClaimsData()
	const showOnboardingRulesTabLocalEnv = isLocalEnv() && isBankAdminOrOperationsUser
	const showOnboardingRulesTabSandboxEnv =
		isSandboxEnv() && isBankAdminOrOperationsUser && sandboxBankIds.includes(claims.bankId)
	const showOnboardingRulesTab = showOnboardingRulesTabLocalEnv || showOnboardingRulesTabSandboxEnv
	return (
		<>
			<TitleBar title={"Bank Settings"} className={"title-bar-no-border"} />
			<nav className="bank-settings tabs-container">
				<div className="tabs">
					<ul>
						<li className={classNames(currentTab == "Legal" && "is-active")} onClick={() => setCurrentTab("Legal")}>
							<a>Legal</a>
						</li>
						<li className={classNames(currentTab == "SAML" && "is-active")} onClick={() => setCurrentTab("SAML")}>
							<a>SAML</a>
						</li>
						{showOnboardingRulesTab && (
							<li
								className={classNames(currentTab == "Risk Settings" && "is-active")}
								onClick={() => setCurrentTab("Risk Settings")}
							>
								<a>Onboarding Rules</a>
							</li>
						)}
						<li
							className={classNames(currentTab == "Payment Rules" && "is-active")}
							onClick={() => setCurrentTab("Payment Rules")}
						>
							<a>Payment Rules</a>
						</li>
					</ul>
				</div>
			</nav>

			<MainSection>
				<TabContent currentTab={currentTab} refresh={refresh} bankSettings={bankSettings} />
			</MainSection>
		</>
	)
}

export default function BankSettingsPage() {
	const accessToken = useAccessToken()
	const claims = useUserClaimsData()
	const [refreshToken, refresh] = useRefreshToken()
	const [currentTab, setCurrentTab] = useQueryState<BankSettingsTabs>("tab", "Legal")
	const bankSettingsAsync = useAsyncResult(() => getByToken(accessToken, claims), [refreshToken])

	return (
		<AsyncResultComponent asyncResult={bankSettingsAsync}>
			{({value: bankSettings}) => {
				return (
					<BankSettingsInternal
						bankSettings={bankSettings}
						refresh={refresh}
						currentTab={currentTab}
						setCurrentTab={(t) => {
							setCurrentTab(t)
							refresh()
						}}
					/>
				)
			}}
		</AsyncResultComponent>
	)
}
